// 장바구니 한 줄(cart.ts의 CartLine)을 최신 서버 상품 데이터와 대조해
// 검증한다. 장바구니는 상품 id/옵션 id/수량만 들고 있으므로, 이 함수가
// "지금 이 줄을 그대로 주문할 수 있는가"를 판단하는 유일한 곳이다.
// 장바구니 페이지와 주문서(카트 모드) 양쪽이 이 로직을 공유해야 두 화면이
// 같은 기준으로 문제를 판단한다.
import type { CartLine } from "@/lib/cart";
import type { ProductOptionWithPrice, ProductWithDiscount } from "@/types";

export type CartLineProblem =
  | "not_found" // 상품이 삭제됨(서버가 명확히 404를 응답함)
  | "unavailable" // 판매 중단(isAvailable=false)
  | "option_unavailable" // 담을 때 고른 옵션이 사라졌거나 판매 중단됨
  | "out_of_stock" // 재고가 담은 수량보다 적음(예약 상품 제외)
  | "below_minimum" // 최소 주문 수량 미달
  | "unverified" // 네트워크·서버 오류로 확인 자체를 못함(상품이 없다는 뜻이 아님)
  | "preorder_mixed"; // 예약 상품이 일반 상품과 같은 장바구니에 섞여 있음

// 상품 조회 결과. "찾을 수 없음(404)"과 "확인하지 못함(네트워크·5xx 등)"을
// 구분해야 한다 — 후자를 전자로 뭉치면, 연결이 불안정한 손님에게 멀쩡한
// 상품을 "더 이상 판매하지 않는다"고 잘못 알리게 된다. 실제 조회는
// useValidatedCart.ts가 하고, 이 타입으로 그 결과를 이 함수에 전달한다.
export type ProductFetchStatus = "found" | "not_found" | "unverified";

export interface ProductFetchResult {
  status: ProductFetchStatus;
  // status가 "found"일 때만 값이 있다.
  product: ProductWithDiscount | null;
}

export interface ValidatedCartLine {
  cartLine: CartLine;
  product: ProductWithDiscount | null;
  option: ProductOptionWithPrice | null;
  problem: CartLineProblem | null;
  // 서버 기준 단가(할인 반영). 가격을 확정할 수 없는 문제(not_found,
  // unavailable, option_unavailable, unverified)면 0.
  unitPrice: number;
}

export function validateCartLine(
  cartLine: CartLine,
  fetchResult: ProductFetchResult,
): ValidatedCartLine {
  if (fetchResult.status === "unverified") {
    return {
      cartLine,
      product: null,
      option: null,
      problem: "unverified",
      unitPrice: 0,
    };
  }

  const product = fetchResult.product;
  if (fetchResult.status === "not_found" || !product) {
    return {
      cartLine,
      product: null,
      option: null,
      problem: "not_found",
      unitPrice: 0,
    };
  }

  if (!product.isAvailable) {
    return {
      cartLine,
      product,
      option: null,
      problem: "unavailable",
      unitPrice: 0,
    };
  }

  const option = product.hasOptions
    ? (product.options.find((o) => o.id === cartLine.optionId) ?? null)
    : null;

  if (product.hasOptions && (!option || !option.isAvailable)) {
    return {
      cartLine,
      product,
      option: null,
      problem: "option_unavailable",
      unitPrice: 0,
    };
  }

  const unitPrice = option ? option.finalPrice : product.finalPrice;
  const stock = option ? option.stock : product.stock;

  if (!product.isPreorder && stock < cartLine.quantity) {
    return { cartLine, product, option, problem: "out_of_stock", unitPrice };
  }

  if (cartLine.quantity < product.minOrderQuantity) {
    return { cartLine, product, option, problem: "below_minimum", unitPrice };
  }

  return { cartLine, product, option, problem: null, unitPrice };
}

export const CART_LINE_PROBLEM_LABELS: Record<CartLineProblem, string> = {
  not_found: "더 이상 판매하지 않는 상품입니다",
  unavailable: "판매가 중단된 상품입니다",
  option_unavailable: "선택하신 옵션을 더 이상 선택할 수 없습니다",
  out_of_stock: "재고가 부족합니다",
  below_minimum: "최소 주문 수량에 못 미칩니다",
  unverified: "상품 정보를 확인하지 못했습니다. 다시 시도해 주세요.",
  preorder_mixed:
    "예약 상품은 일반 상품과 함께 주문할 수 없습니다. 따로 주문해 주세요.",
};

// 예약 상품은 수확 후 순차 발송이라 일반 상품과 발송일이 다르다(주문
// API도 예약 상품이 하나라도 있으면 주문 전체를 예약 발송으로 표시하고,
// 그 발송 예정일을 기록한다 — src/app/api/orders/route.ts). 장바구니에
// 예약 상품과 일반 상품이 함께 있으면 일반 상품까지 예약 발송으로 잘못
// 안내되므로, 여기서 미리 막는다.
//
// validateCartLine()은 한 줄씩만 보므로 "장바구니 전체 구성"을 판단할 수
// 없다. 그래서 모든 줄을 검증한 뒤 이 함수로 한 번 더 훑어, 다른 문제가
// 없는(problem === null) 예약 상품 줄에만 preorder_mixed를 추가로 표시한다
// — 이미 판매 중단·확인 실패 등 더 근본적인 문제가 있는 줄은 그 문제를
// 그대로 둔다(재고 부족은 예약 상품에는 애초에 적용되지 않는다 —
// validateCartLine 참고). 일반 상품 줄은 따로 표시하지 않아도 이 문제
// 하나로 체크아웃 전체가 막히므로 충분하다.
export function flagMixedPreorderLines(
  lines: ValidatedCartLine[],
): ValidatedCartLine[] {
  const resolvedProducts = lines
    .map((line) => line.product)
    .filter((product) => product !== null);
  const hasPreorderProduct = resolvedProducts.some((p) => p.isPreorder);
  const hasRegularProduct = resolvedProducts.some((p) => !p.isPreorder);
  if (!hasPreorderProduct || !hasRegularProduct) return lines;

  return lines.map((line) =>
    line.problem === null && line.product?.isPreorder
      ? { ...line, problem: "preorder_mixed" }
      : line,
  );
}
